import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const ApplicationDevelopment = () => {
  const phrases = [
    "Software Engineering",
    "Application Development"
  ];

  const cardData = [
    { type: 'purple-clay', content: '💻' },
    { type: 'smooth-wave', content: '📱' },
    { type: 'abstract-blocks', content: '🚀' },
    { type: 'pink-sphere', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🌐", title: "Custom Web Application Development", desc: "Build feature-rich, dynamic web systems with highly responsive front-ends and reliable, secure back-ends." },
    { icon: "📱", title: "Cross-Platform Mobile Apps", desc: "Engineer seamless native experiences across iOS and Android systems using single-codebase frameworks." },
    { icon: "🏗️", title: "Robust Enterprise Software", desc: "Design and implement high-availability architectures engineered to scale enterprise processes reliably." },
    { icon: "🧩", title: "API Design & Microservices", desc: "Deconstruct systems into loosely coupled microservices connected with secure, high-throughput RESTful or GraphQL APIs." },
    { icon: "🎨", title: "UI/UX Product Engineering", desc: "Transform concepts into beautiful digital experiences combining wireframes, clickable layouts, and clean visual patterns." },
    { icon: "🔄", title: "Legacy App Modernization", desc: "Re-architect monolithic architectures into cloud-optimized platforms without operational or structural data loss." },
    { icon: "🔐", title: "Advanced Security Hardening", desc: "Inject strict authentication protocols, encryption standards, and threat tracking guards natively into apps." }
  ];

  const advancedServices = [
    { icon: "⚡", title: "Real-Time Systems & Streaming", desc: "Integrate ultra-low latency websocket connections and active event engines into operational displays." },
    { icon: "☁️", title: "Cloud-Native Infrastructure Development", desc: "Deploy highly scalable serverless architectures, managed computing nodes, and scalable auto-scaling scripts." },
    { icon: "🤖", title: "AI & Smart System Integration", desc: "Embed custom machine learning prediction engines, data models, and specialized natural language nodes." },
    { icon: "🗄️", title: "Multi-Engine Database Design", desc: "Structure scalable relational schemas alongside high-performance unstructured NoSQL memory tiers." },
    { icon: "📈", title: "Analytical Processing & Telemetry", desc: "Build dynamic metric dashboards to observe consumer journeys and capture core workflow metrics." },
    { icon: "📦", title: "Continuous CI/CD Delivery", desc: "Implement robust automated pipelines ensuring error-free version changes from main code bases into live space." },
    { icon: "🛡️", title: "Compliance-First Architecture", desc: "Align codebases with complex regulatory standards, securing user data rules natively across nodes." },
    { icon: "🏁", title: "Performance & Load Auditing", desc: "Stress-test application environments to resolve computing bottlenecks and maintain sub-second operations." },
    { icon: "🤝", title: "Custom CRM & ERP Bridging", desc: "Unify external functional modules smoothly with corporate hubs using structured automation webhooks." }
  ];

  const technologies = {
    "Front-End Engineering": ["React.js / Next.js", "Angular / Vue.js", "HTML5 / CSS3 / Sass", "Tailwind CSS / Bootstrap"],
    "Back-End Frameworks": ["Node.js (Express)", "Python (Django / Fast外部)", "Java Spring Boot", "C# .NET Core"],
    "Mobile Development": ["React Native", "Flutter", "Swift (iOS)", "Kotlin (Android)"],
    "Database Systems": ["PostgreSQL / MySQL", "MongoDB / DynamoDB", "Redis Caching Tier", "Elasticsearch Engine"],
    "Cloud & Devops Stack": ["AWS / Microsoft Azure", "Docker Containers", "GitHub Actions / GitLab", "Vercel / Heroku Platforms"]
  };

  const processSteps = [
    { num: "01", title: "Requirement Blueprinting", desc: "Evaluating current functional targets, consumer journeys, and critical architectural milestones." },
    { num: "02", title: "System Engineering", desc: "Designing comprehensive data schemas, technical connection patterns, and boundary access layouts." },
    { num: "03", title: "Agile Code Execution", desc: "Writing clean, modular components alongside robust server routing tracks in targeted sprint cycles." },
    { num: "04", title: "System Synthesis", desc: "Connecting dynamic user interfaces to database nodes and integrating reliable external tool frameworks." },
    { num: "05", title: "Rigorous Testing & QA", desc: "Running unit validation profiles, cross-browser displays, and complete end-to-end load scripts." },
    { num: "06", title: "Production Deployment", desc: "Publishing verified builds onto scalable cloud ecosystems with zero operational disruptions." },
    { num: "07", title: "Evolution & Optimization", desc: "Monitoring platform health indicators, refining slow queries, and introducing version upgrades." }
  ];

  const industries = [
    "E-Commerce & Digital Malls", "FinTech & Secure Banking", "Healthcare & Telemedicine", "SaaS & High-Tech Startups",
    "Logistics & Fleet Logistics", "EdTech & Learning Networks", "Real Estate Platforms", "Entertainment & Media Hubs",
    "Automotive & Smart Mobility", "Hospitality & Dynamic Booking", "Professional B2B Portals", "Enterprise Manufacturing"
  ];

  const designBenefits = [
    "High-Performance Custom Builds", "Elastic Cross-Platform Scale", "Airtight Zero-Trust Security",
    "Modular Microservice Bases", "Intuitive Executive Interfaces", "Automated Update Delivery",
    "Seamless External Tool Merges", "Complete Code Independence"
  ];

  const whyChooseUs = [
    "Certified Software Engineers", "Custom High-Availability Infrastructure", "Modern UI/UX Performance Visuals",
    "Flawless Cross-Platform Frameworks", "Deep Multi-Language Development Expertise", "Scalable Multi-Tier System Deployments",
    "Clean, Extensible Object Coding", "Transparent Agile Delivery Cycles", "Proven Database Schema Normalization",
    "Dedicated Post-Launch Lifecycle Care", "Automation-First Engineering Mindset", "100% IP & Source Ownership Transfer"
  ];

  return (

    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          Application Development Services | Custom Software Development | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Application Development Services | Custom Software Development | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides professional application development services, including custom software development, enterprise applications, web applications, cloud-based solutions, API integration, application modernization, UI/UX design, and scalable business applications."
        />

        <meta
          name="keywords"
          content="Application Development, Application Development Services, Custom Application Development, Software Development, Custom Software Development, Enterprise Application Development, Web Application Development, Business Application Development, Cloud Application Development, API Integration, Application Modernization, Full Stack Development, UI UX Design, Scalable Applications, Enterprise Software Solutions, Application Maintenance, Software Development Company, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/application-development"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Application Development Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Build secure, scalable, and high-performance applications with Affsoft IT Solutions Pvt. Ltd. We specialize in custom software development, enterprise applications, cloud solutions, API integration, and digital transformation."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/application-development"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Application Development Services | Custom Software Development | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Transform your business with custom application development services from Affsoft IT Solutions Pvt. Ltd. We deliver enterprise software, web applications, cloud solutions, and scalable business applications."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="application-development-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0c0b1e' // dark application context fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.15 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>

          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Software Engineering &nbsp; and &nbsp;</span>
              <span>Application Development </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-primary fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Application Development</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we transform abstract business definitions into dynamic, production-grade applications. Our advanced software frameworks deliver seamless multi-platform responsive styling, stable database setups, and optimized processing models to outpace competition and turn concepts into scalable engines.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About Affsoft</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-primary fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SERVICE SOLUTIONS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Product Architecture</span>
              <h2 className="fw-bold text-dark display-6">Custom Software Formulated for Scalable Operations</h2>
              <p className="text-muted mt-3">
                Rigid off-the-shelf software solutions limit continuous operational adaptation. At Affsoft IT Solutions Pvt. Ltd., we design secure system structures that match your business pathways, giving you total functional flexibility.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Software Benefits Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Build Tailored App Frameworks?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED APPLICATION OPERATIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-success px-3 py-2 rounded-pill mb-2 text-uppercase">Advanced Subsystems</span>
              <h2 className="fw-bold text-dark display-6">High-Performance Platforms Engineered for Speed</h2>
              <p className="text-muted mt-3">
                We leverage clean scripting standards to automate information transfers across business systems. From distributed processing pipelines to modern analytics extensions, our development builds reliable technical assets designed to hold up.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-success">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-warning text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Development Stack</span>
              <h2 className="fw-bold display-6">Languages & Frameworks We Leverage</h2>
              <p className="text-light-50">We build and validate complex source operations using trusted modern developer libraries.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-warning mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-warning opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Development Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Software Flow</h2>
              <p className="text-muted">A clear milestone matrix guiding abstract specifications into deployable platforms.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-primary border-4 h-100 shadow-sm">
                    <div className="fw-bold text-primary display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We coordinate programmatic clean coding structures with cloud architecture metrics to scale apps safely.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-primary fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default ApplicationDevelopment;